/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { delayedResponse } from '../companies/companiesMock';
import { makeMockResponse } from '../mockUtils';
import {
    CashBoxTaskItemDto,
    CashBoxTasksListDto,
    CashBoxTaskStatus,
    CashBoxTaskType,
    RoughCashboxesListDto,
    SuitableTargetVersionsDto,
    DeviceServiceListItem,
    DevicePlatform,
} from './serviceClient.typings';
import {
    cashBoxCertUpdateTaskItemFactory,
    cashBoxChecksumTaskItemFactory,
    cashBoxEnvInfoTaskItemFactory,
    cashBoxFwUpdateTaskItemFactory,
    cashBoxLogsTaskItemFactory,
    createCashboxTaskResponseFactory,
    roughCashboxesListItemFactory,
    deviceDtoFactory,
    cashBoxDumpTaskItemFactory,
    cashBoxRepairTaskItemFactory,
} from './serviceFactories';

const GET_ROUGH_CASHBOXES_URL: RegExp = /\/service\/cashboxes$/;
const CREATE_CASHBOX_TASK = /\/service\/tasks$/;
const CREATE_CASHBOXES_TASKS = /\/service\/tasks\/multi$/;
const GET_TASK_HISTORY = /\/service\/tasks\/history$/;
const GET_TASKS_STATUS = /\/service\/tasks\/status$/;
const SUITABLE_TARGET_VERSIONS = /\/v2\/firmwares/;
const SERVICE_UPLOAD_FILE = /\/service\/uploadInitFile/;
const SEARCH_FACTORY_NUMBERS = /\/service\/factoryNumbers/;
const DEVICES_LIST = /\/service\/devices/;
const DEVICE_OPERATION = /\/service\/deviceOperation/;

export function addServiceMockApi(mockAdapter: MockAdapter): void {
    mockAdapter.onPost(GET_ROUGH_CASHBOXES_URL).reply((config) => {
        const body = JSON.parse(config.data);
        const data: RoughCashboxesListDto = {
            afterKeyTaxNumber: '234318932',
            cashBoxes: roughCashboxesListItemFactory.list({ count: body.limit }),
        };
        roughCashboxesListItemFactory.resetCount();
        return delayedResponse(makeMockResponse(200, data, config), 300);
    });

    mockAdapter.onPost(CREATE_CASHBOX_TASK).reply((config) => {
        const data = createCashboxTaskResponseFactory.item();
        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 3000);
    });

    mockAdapter.onPost(CREATE_CASHBOXES_TASKS).reply((config) => {
        const body = JSON.parse(config.data);
        const data = createCashboxTaskResponseFactory.list({ count: body.length });
        createCashboxTaskResponseFactory.resetCount();
        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 3000);
    });

    mockAdapter.onGet(GET_TASK_HISTORY).reply((config) => {
        let items: CashBoxTaskItemDto[] = [];
        switch (config.params.name) {
            case CashBoxTaskType.CERTUPDATE:
                items = cashBoxCertUpdateTaskItemFactory.list({ count: config.params.limit });
                if (items[0].id === '10001') items[0].status = CashBoxTaskStatus.CREATED;
                break;
            case CashBoxTaskType.FWUPDATE:
                items = cashBoxFwUpdateTaskItemFactory.list({ count: config.params.limit });
                break;
            case CashBoxTaskType.LOGS:
                items = cashBoxLogsTaskItemFactory.list({ count: config.params.limit });
                break;
            case CashBoxTaskType.ENVINFO:
                items = cashBoxEnvInfoTaskItemFactory.list({ count: config.params.limit });
                break;
            case CashBoxTaskType.CHECKSUM:
                items = cashBoxChecksumTaskItemFactory.list({ count: config.params.limit });
                break;
        }
        const data: CashBoxTasksListDto = {
            count: config.params.limit * 3,
            items,
        };
        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 300);
    });

    mockAdapter.onGet(GET_TASKS_STATUS).reply((config) => {
        let items: CashBoxTaskItemDto[] = [];
        const count = config.params.cashBoxTaxNumbers.length;
        switch (config.params.name) {
            case CashBoxTaskType.CERTUPDATE:
                items = cashBoxCertUpdateTaskItemFactory.list({ count });
                cashBoxCertUpdateTaskItemFactory.resetCount();
                break;
            case CashBoxTaskType.FWUPDATE:
                items = cashBoxFwUpdateTaskItemFactory.list({ count });
                cashBoxFwUpdateTaskItemFactory.resetCount();
                break;
            case CashBoxTaskType.LOGS:
                items = cashBoxLogsTaskItemFactory.list({ count });
                cashBoxLogsTaskItemFactory.resetCount();
                break;
            case CashBoxTaskType.ENVINFO:
                items = cashBoxEnvInfoTaskItemFactory.list({ count });
                cashBoxEnvInfoTaskItemFactory.resetCount();
                break;
            case CashBoxTaskType.CHECKSUM:
                items = cashBoxChecksumTaskItemFactory.list({ count });
                cashBoxChecksumTaskItemFactory.resetCount();
                break;
            case CashBoxTaskType.DUMP:
                items = cashBoxDumpTaskItemFactory.list({ count });
                cashBoxDumpTaskItemFactory.resetCount();
                break;
            case CashBoxTaskType.REPAIR:
                items = cashBoxRepairTaskItemFactory.list({ count });
                cashBoxRepairTaskItemFactory.resetCount();
                break;
        }
        items.forEach((item, index) => (item.cashBoxTaxNumber = config.params.cashBoxTaxNumbers[index]));
        const data: CashBoxTasksListDto = { count, items };
        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 300);
    });

    mockAdapter.onGet(SUITABLE_TARGET_VERSIONS).reply((config) => {
        const platformType = config.url.split('/')[2];
        const data: SuitableTargetVersionsDto = {
            versions: [
                '3.31.0.stm',
                '2.23.0.stm',
                '2.25.0.stm',
                '2.26.0.stm',
                '2.29.1.stm',
                '4.28.0',
                '2.27.0',
                '2.30.0.stm',
                '2.22.1.stm',
                '2.24.0.stm',
                '4.27.1.stm',
            ],
        };
        if (platformType === DevicePlatform.LPC)
            data.versions = [
                '4.22.1.lpc',
                '2.23.0.lpc',
                '3.25.0.lpc',
                '4.28.0',
                '2.27.0',
                '4.26.0.lpc',
                '3.29.1.lpc',
                '2.28.0.lpc',
            ];

        return delayedResponse(makeMockResponse(200, data, config), 3000);
    });

    mockAdapter.onPost(SERVICE_UPLOAD_FILE).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), 1000);
    });
    mockAdapter.onGet(SEARCH_FACTORY_NUMBERS).reply((config) => {
        const { factoryNumber, limit } = config.params;
        const arr = Array(limit).fill(0);
        const data = arr.map(() => factoryNumber + (Math.random() * 100000).toFixed());

        return delayedResponse(makeMockResponse(200, data, config), 500);
    });

    mockAdapter.onPost(DEVICES_LIST).reply((config) => {
        const body = JSON.parse(config.data);
        const data: DeviceServiceListItem[] = deviceDtoFactory(body.factoryNumbers).list({
            count: body.factoryNumbers.length,
        });
        return delayedResponse(makeMockResponse(200, data, config), 500);
    });

    mockAdapter.onPost(DEVICE_OPERATION).reply((config) => {
        return delayedResponse(makeMockResponse(200, {}, config), 500);
    });
}
